/**
 * @file Browser and DOM observation helpers (for observing elements, performance, etc).
 */

// A record of all selectors that we are currently waiting on.
interface AwaitedElementRegistration {
  selector: string;
  callback: (element: HTMLElement) => void;
  root: HTMLElement | Document;
}

const awaitedElementRegistry: AwaitedElementRegistration[] = [];
let awaitedElementMutationObserver: MutationObserver | null = null;

/**
 * The shared mutation observer watches for all awaited elements defined in `awaitedElementRegistry`.
 */
function sharedMutationObserver() {
  // Iterate backward to allow for removal of elements from the registry.
  let index = awaitedElementRegistry.length;
  while (index--) {
    const registration = awaitedElementRegistry[index];
    const awaitedElement = (
      registration.root instanceof Document ? registration.root.body : registration.root
    ).querySelector<HTMLElement>(registration.selector);
    if (awaitedElement) {
      // Execute callback for found selector and remove registration from the list.
      try {
        registration.callback(awaitedElement);
      } catch (error) {
        console.error(
          "Error executing callback for awaited element selector in sharedMutationObserver:",
          registration.selector,
          error
        );
      }
      awaitedElementRegistry.splice(index, 1);
    }
  }

  // If the registry becomes empty, we disconnect the observer to reduce overhead. It will be recreated if needed.
  if (awaitedElementRegistry.length === 0 && awaitedElementMutationObserver) {
    awaitedElementMutationObserver.disconnect();
    awaitedElementMutationObserver = null;
  }
}

/**
 * Initialize the shared mutation observer if it is not already running.
 */
function initializeSharedMutationObserver() {
  if (awaitedElementMutationObserver) return; // Already initialized

  // Attempt to find elements for each waiting selector.
  awaitedElementMutationObserver = new MutationObserver(sharedMutationObserver);

  // Apply the observer to the entire document body.
  awaitedElementMutationObserver.observe(document.body, {
    childList: true,
    subtree: true
  });
}

/**
 * Wait for a specific element matching `selector` to appear in the DOM an execute a callback.
 *
 * @param selector - The CSS selector to wait for.
 * @param callback - The function to execute when the element is found.
 * @param root - The root element to search within (default: `document`).
 */
export function waitForElement(
  selector: string,
  callback: (element: HTMLElement) => void,
  root: HTMLElement | Document = document
): void {
  const existingElement = (root instanceof Document ? root.body : root).querySelector<HTMLElement>(
    selector
  );
  if (existingElement) {
    try {
      callback(existingElement);
    } catch (error) {
      console.error(
        "Error executing callback for existing element selector in waitForElement:",
        selector,
        error
      );
    }
    return;
  }

  // Add this selector to the global wait list
  awaitedElementRegistry.push({ selector, callback, root });

  // Ensure the global mutation observer is running
  initializeSharedMutationObserver();
}

/**
 * Observe when a specific element becomes visible in the viewport for the first time and execute a callback.
 */
export function observeVisibilityOncePerPageView(
  element: HTMLElement,
  callback: () => void,
  options: IntersectionObserverInit = { threshold: 1 }
): void {
  const observer = new IntersectionObserver((entries, obs) => {
    for (const entry of entries) {
      if (entry.isIntersecting) {
        try {
          callback();
        } catch (error) {
          console.error("Error executing callback in observeVisibilityOncePerPageView:", error);
        }
        obs.disconnect();
        break;
      }
    }
  }, options);

  observer.observe(element);
}

/**
 * Observes the First Contentful Paint (FCP) performance entry and triggers a callback when it is recorded.
 *
 * @param callback - The function to be called when the FCP entry is observed.
 */
export function observeFCP(callback: (performanceEntry: PerformanceEntry) => void): void {
  const observer = new PerformanceObserver((list) => {
    const entries = list.getEntriesByName("first-contentful-paint");
    if (entries && entries.length > 0) {
      observer.disconnect();
      try {
        callback(entries[0]);
      } catch (error) {
        console.error("Error executing callback in observeFCP:", error);
      }
    }
  });

  observer.observe({ type: "paint", buffered: true });
}

/**
 * Observes the First Contentful Paint (FCP) event once per session and executes a callback function.
 *
 * @param callback - The function to be executed when the FCP event is observed.
 */
export function observeFCPOncePerSession(
  callback: (performanceEntry: PerformanceEntry) => void
): void {
  if (sessionStorage.getItem("fcpInitialTimestamp")) {
    return; // Already set for this session
  }

  observeFCP((performanceEntry) => {
    sessionStorage.setItem("fcpInitialTimestamp", Date.now().toString());
    try {
      callback(performanceEntry);
    } catch (error) {
      console.error(
        "Error executing callback with performanceEntry in observeFCPOncePerSession:",
        performanceEntry,
        error
      );
    }
  });
}
